import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ChangePasswordModal from '../ChangePasswordModal';

const BACKEND_URL = process.env.REACT_APP_API_URL;

function YoutubeLite({ id, title }: { id: string; title: string }) {
  const [active, setActive] = useState(false);
  return (
    <div className="relative w-full cursor-pointer rounded-xl overflow-hidden" style={{ paddingBottom: '56.25%' }} onClick={() => setActive(true)}>
      {active ? (
        <iframe
          className="absolute inset-0 w-full h-full"
          src={`https://www.youtube.com/embed/${id}?autoplay=1`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      ) : (
        <>
          <img
            className="absolute inset-0 w-full h-full object-cover"
            src={`https://img.youtube.com/vi/${id}/maxresdefault.jpg`}
            alt={title}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-red-600 hover:bg-red-500 transition-colors rounded-xl px-4 py-2 flex items-center gap-2 shadow-lg">
              <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
              <span className="text-white text-sm font-semibold">Play</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

interface Bowler {
  id: number;
  name: string;
  email: string;
  playerId: number;
  joinedAt: string;
  leaguesCount: number;
  leagueNames: string;
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalBowlers: number;
  limit: number;
}

interface BowlersData {
  bowlers: Bowler[];
  pagination: PaginationInfo;
}

interface ScoutSettings {
  hdcp_base_average: number;
  hdcp_percentage: number;
  hdcp_max: number;
}

const ScoutDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [bowlersData, setBowlersData] = useState<BowlersData | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState<string>('');
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [scoutSettings, setScoutSettings] = useState<ScoutSettings>({
    hdcp_base_average: 200,
    hdcp_percentage: 80,
    hdcp_max: 40
  });
  const [savingSettings, setSavingSettings] = useState(false);

  const fetchBowlers = async () => {
    try {
      setLoading(true);
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const searchParams = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20'
      });

      if (searchTerm.trim()) {
        searchParams.append('search', searchTerm.trim());
      }

      const response = await fetch(`${apiUrl}/scout/bowlers?${searchParams}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBowlersData(data);
        setError('');
      } else {
        setError('Failed to fetch bowlers data');
      }
    } catch (error) {
      console.error('Failed to fetch bowlers:', error);
      setError('Failed to fetch bowlers data');
    } finally {
      setLoading(false);
    }
  };

  const fetchScoutSettings = async () => {
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/scout/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.scout) {
          setScoutSettings({
            hdcp_base_average: data.scout.hdcp_base_average,
            hdcp_percentage: parseFloat(data.scout.hdcp_percentage),
            hdcp_max: data.scout.hdcp_max
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch scout settings:', error);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSavingSettings(true);
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/scout/auth/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(scoutSettings)
      });

      if (response.ok) {
        alert(t('scout.dashboard.settingsSaved'));
        setShowSettingsModal(false);
      } else {
        alert(t('scout.dashboard.settingsFailed'));
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      alert(t('scout.dashboard.settingsFailed'));
    } finally {
      setSavingSettings(false);
    }
  };

  useEffect(() => {
    fetchBowlers();
    fetchScoutSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchTerm]);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleBowlerClick = (bowlerId: number) => {
    navigate(`/scout/bowlers/${bowlerId}`);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/');
  };

  const renderPagination = () => {
    if (!bowlersData || bowlersData.pagination.totalPages <= 1) return null;

    const { currentPage, totalPages } = bowlersData.pagination;
    const pages = [];

    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, startPage + 4);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="flex items-center justify-center space-x-2 mt-6">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-3 py-2 rounded-lg bg-white/10 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/20 transition duration-200"
        >
          {t('scout.dashboard.previous')}
        </button>
        
        {pages.map(page => (
          <button
            key={page}
            onClick={() => handlePageChange(page)}
            className={`px-3 py-2 rounded-lg transition duration-200 ${
              page === currentPage
                ? 'bg-emerald-600 text-white font-bold'
                : 'bg-white/10 text-white hover:bg-white/20'
            }`}
          >
            {page}
          </button>
        ))}
        
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-3 py-2 rounded-lg bg-white/10 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/20 transition duration-200"
        >
          {t('scout.dashboard.next')}
        </button>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <h1 className="text-3xl font-bold text-white">{t('scout.dashboard.title')}</h1>
            </div>
            <div className="flex items-center space-x-4">
              
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition duration-200"
              >
                {t('common.logout')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <p className="text-emerald-200 text-lg mb-8">{t('scout.dashboard.subtitle')}</p>

        {/* Tutorial card */}
        <div className="mb-8 max-w-sm">
          <div className="flex items-center gap-2 mb-2">
            <div className="bg-red-100 rounded-lg p-1.5 flex-shrink-0">
              <svg className="w-3.5 h-3.5 text-red-600" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
              </svg>
            </div>
            <p className="text-white text-sm font-semibold uppercase tracking-widest">Scout Tutorial</p>
          </div>
          <div className="bg-white/10 border border-white/20 rounded-2xl overflow-hidden">
          
            <YoutubeLite id="JZNznDdzHZE" title="Side Action Tutorial" />
          </div>
        </div>

        {/* Search and Stats */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 space-y-4 sm:space-y-0">
          <div className="flex-1 max-w-md">
            <input
              type="text"
              placeholder={t('scout.dashboard.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-emerald-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
            <p className="text-emerald-300 text-xs mt-1">
              {t('scout.dashboard.searchHint')}
            </p>
          </div>
          
          {bowlersData && (
            <div className="flex items-center space-x-4">
              <div className="bg-white/10 backdrop-blur-lg rounded-lg px-4 py-2 border border-white/20">
                <span className="text-emerald-200 text-sm">{t('scout.dashboard.totalBowlers')}: </span>
                <span className="text-white font-bold">{bowlersData.pagination.totalBowlers}</span>
              </div>
              <div className="bg-white/10 backdrop-blur-lg rounded-lg px-4 py-2 border border-white/20">
                <span className="text-emerald-200 text-sm">{t('scout.dashboard.page')}: </span>
                <span className="text-white font-bold">
                  {bowlersData.pagination.currentPage} of {bowlersData.pagination.totalPages}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-500/20 border border-red-400 rounded-lg p-4 mb-6">
            <p className="text-red-200">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 border border-white/20 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
            <p className="text-white">{t('scout.dashboard.loadingBowlers')}</p>
          </div>
        )}

        {/* Bowlers List */}
        {!loading && bowlersData && (
          <>
            {bowlersData.bowlers.length === 0 ? (
              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 border border-white/20 text-center">
                <div className="text-4xl mb-2">👥</div>
                {searchTerm ? (
                  <>
                    <p className="text-emerald-200">{t('scout.dashboard.noBowlersFound', { searchTerm })}</p>
                    <p className="text-emerald-300 text-sm">{t('scout.dashboard.tryAdjusting')}</p>
                  </>
                ) : (
                  <>
                    <p className="text-emerald-200">{t('scout.dashboard.noBowlersYet')}</p>
                    <p className="text-emerald-300 text-sm">{t('scout.dashboard.bowlersWillAppear')}</p>
                  </>
                )}
              </div>
            ) : (
              <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 overflow-hidden">
                <div className="max-h-[40em] overflow-y-auto">
                  <div className="divide-y divide-white/10">
                    {bowlersData.bowlers.map((bowler) => (
                      <div
                        key={bowler.id}
                        onClick={() => handleBowlerClick(bowler.id)}
                        className="p-4 hover:bg-white/10 transition duration-200 cursor-pointer"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full flex items-center justify-center">
                              <span className="text-white font-bold text-lg">
                                {bowler.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-white">{bowler.name}</h3>
                              <p className="text-emerald-200 text-sm">{bowler.email}</p>
                              <div className="flex items-center space-x-4 mt-1 text-xs text-emerald-300">
                                <span>{t('scout.dashboard.bowlerId')}: {bowler.id}</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-4">
                            <div className="text-center">
                              <p className="text-lg font-bold text-white">{bowler.leaguesCount}</p>
                              <p className="text-emerald-200 text-xs">{t('scout.dashboard.leagues')}</p>
                            </div>
                            <div className="text-emerald-300 max-w-xs truncate">
                              <p className="text-sm">{bowler.leagueNames}</p>
                            </div>
                            <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Pagination */}
                {renderPagination()}
              </div>
            )}
          </>
        )}
      </div>

      {/* Account Info */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 mt-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-white">Account Information</h3>
            <button
              onClick={() => setShowChangePasswordModal(true)}
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 px-4 py-2 rounded-lg transition group"
            >
              <svg className="w-6 h-6 text-white/60 group-hover:rotate-45 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="text-sm font-semibold text-white/70 group-hover:text-white transition">Change Password</span>
            </button>
          </div>
          <div className="text-emerald-200">
            <p><strong className="text-white">Role:</strong> Scout</p>
          </div>
        </div>
      </div>

      {showChangePasswordModal && (
        <ChangePasswordModal accentColor="emerald" onClose={() => setShowChangePasswordModal(false)} />
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-6 rounded-t-xl">
              <h2 className="text-xl font-bold">{t('scout.dashboard.customHandicapSettings')}</h2>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  {t('scout.dashboard.baseAverage')}
                </label>
                <input
                  type="number"
                  value={scoutSettings.hdcp_base_average}
                  onChange={(e) => setScoutSettings({ ...scoutSettings, hdcp_base_average: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  {t('scout.dashboard.percentage')}
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={scoutSettings.hdcp_percentage}
                  onChange={(e) => setScoutSettings({ ...scoutSettings, hdcp_percentage: parseFloat(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  {t('scout.dashboard.maxHandicap')}
                </label>
                <input
                  type="number"
                  value={scoutSettings.hdcp_max}
                  onChange={(e) => setScoutSettings({ ...scoutSettings, hdcp_max: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>

            <div className="flex space-x-3 p-6 bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setShowSettingsModal(false)}
                className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-lg transition duration-200"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSaveSettings}
                disabled={savingSettings}
                className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold rounded-lg transition duration-200"
              >
                {savingSettings ? t('common.Processing') : t('scout.dashboard.saveSettings')}
              </button>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
};

export default ScoutDashboard;
